import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapPin, Pencil, Plus, Trash2, Truck } from "lucide-react";
import { friendlyError } from "@/lib/errors";
import { formatMoney } from "@/lib/format";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { Modal } from "@/components/ui/Modal";
import { StatusPill } from "@/components/ui/StatusPill";
import { QueryBoundary } from "@/components/ui/QueryBoundary";
import { EmptyState } from "@/components/ui/states";
import { Field, FormGrid, Input, Toggle } from "@/components/ui/Form";
import { toast } from "@/components/ui/toast";
import {
  VACIA,
  aBorrador,
  createRate,
  deleteRate,
  fetchRates,
  toggleRate,
  updateRate,
  validar,
  type RateDraft,
  type ShippingRate,
} from "./api";

export function ShippingPage() {
  const { org } = useActiveOrg();
  const qc = useQueryClient();
  const orgId = org?.organizationId ?? null;
  const moneda = org?.currency ?? "PEN";

  const [editando, setEditando] = useState<ShippingRate | null>(null);
  const [creando, setCreando] = useState(false);
  const [borrando, setBorrando] = useState<ShippingRate | null>(null);

  const zonas = useQuery({
    queryKey: ["shipping-rates", orgId],
    queryFn: () => fetchRates(orgId!),
    enabled: Boolean(orgId),
  });

  const refrescar = () => qc.invalidateQueries({ queryKey: ["shipping-rates", orgId] });

  async function cambiarEstado(zona: ShippingRate) {
    try {
      await toggleRate(zona.id, !zona.active);
      void refrescar();
      toast.ok(zona.active ? "Distrito desactivado." : "Distrito activado.");
    } catch (error) {
      toast.error(friendlyError(error));
    }
  }

  async function eliminar() {
    if (!borrando) return;
    try {
      await deleteRate(borrando.id);
      void refrescar();
      toast.ok("Distrito eliminado.");
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setBorrando(null);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-head font-semibold tracking-tight">Entregas</h1>
          <p className="mt-0.5 text-meta text-ink-2">
            A qué distritos reparte la tienda y cuánto cobra por hacerlo.
          </p>
        </div>
        <Button
          variant="primary"
          icon={<Plus className="size-3.5" />}
          onClick={() => setCreando(true)}
        >
          Nuevo distrito
        </Button>
      </header>

      <QueryBoundary
        query={zonas}
        isEmpty={(d) => d.length === 0}
        empty={
          <Panel>
            <EmptyState
              icon={<Truck className="size-5" />}
              title="Sin distritos de reparto"
              description="Mientras no haya ninguno, el checkout solo podrá ofrecer recojo en tienda."
            />
          </Panel>
        }
      >
        {(lista) => (
          <Panel bleed>
            <table className="w-full text-ui">
              <thead className="border-b border-line-2 text-left text-meta text-ink-2">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Distrito</th>
                  <th className="px-4 py-2.5 font-medium">Tarifa</th>
                  <th className="px-4 py-2.5 font-medium">Gratis desde</th>
                  <th className="px-4 py-2.5 font-medium">Tiempo</th>
                  <th className="px-4 py-2.5 font-medium">Estado</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {lista.map((z) => (
                  <tr key={z.id} className="border-b border-line-2 last:border-b-0">
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-1.5 font-medium">
                        <MapPin className="size-3.5 text-ink-3" />
                        {z.district}
                      </span>
                      <span className="mt-0.5 block text-meta text-ink-3">
                        {[z.province, z.department].filter(Boolean).join(" · ")}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">
                      {Number(z.price) === 0 ? "Gratis" : formatMoney(z.price, moneda)}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-ink-2">
                      {z.free_from === null ? "—" : formatMoney(z.free_from, moneda)}
                    </td>
                    <td className="px-4 py-2.5 text-ink-2">
                      {z.estimated_minutes === null ? "—" : `${z.estimated_minutes} min`}
                    </td>
                    <td className="px-4 py-2.5">
                      <button type="button" onClick={() => void cambiarEstado(z)}>
                        <StatusPill tone={z.active ? "ok" : "neutral"}>
                          {z.active ? "Activo" : "Inactivo"}
                        </StatusPill>
                      </button>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setEditando(z)}
                          className="rounded p-1.5 text-ink-2 hover:bg-sunk"
                          aria-label={`Editar ${z.district}`}
                        >
                          <Pencil className="size-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setBorrando(z)}
                          className="rounded p-1.5 text-ink-2 hover:bg-sunk hover:text-crit"
                          aria-label={`Eliminar ${z.district}`}
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}
      </QueryBoundary>

      {(creando || editando) && (
        <RateModal
          zona={editando}
          moneda={moneda}
          onClose={() => {
            setCreando(false);
            setEditando(null);
          }}
          onSaved={() => {
            void refrescar();
            setCreando(false);
            setEditando(null);
          }}
          organizationId={orgId!}
        />
      )}

      {borrando && (
        <Modal
          open
          tone="danger"
          title={`Eliminar ${borrando.district}`}
          onClose={() => setBorrando(null)}
          description={
            <>
              Se quitará del checkout. Los pedidos ya hechos a ese distrito no
              cambian. Si solo quieres dejar de repartir un tiempo, desactívalo en
              lugar de eliminarlo.
            </>
          }
          footer={
            <>
              <Button size="sm" variant="secondary" onClick={() => setBorrando(null)}>
                Cancelar
              </Button>
              <Button size="sm" variant="danger" onClick={() => void eliminar()}>
                Eliminar
              </Button>
            </>
          }
        />
      )}
    </div>
  );
}

function RateModal({
  zona,
  moneda,
  organizationId,
  onClose,
  onSaved,
}: {
  zona: ShippingRate | null;
  moneda: string;
  organizationId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [borrador, setBorrador] = useState<RateDraft>(zona ? aBorrador(zona) : VACIA);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);

  const set = <K extends keyof RateDraft>(campo: K, valor: RateDraft[K]) =>
    setBorrador((b) => ({ ...b, [campo]: valor }));

  async function guardar() {
    const fallos = validar(borrador);
    setErrores(fallos);
    if (Object.keys(fallos).length > 0) return;

    setGuardando(true);
    try {
      if (zona) await updateRate(zona.id, organizationId, borrador);
      else await createRate(organizationId, borrador);
      toast.ok(zona ? "Distrito actualizado." : "Distrito agregado.");
      onSaved();
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={zona ? `Editar ${zona.district}` : "Nuevo distrito"}
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button size="sm" variant="primary" onClick={() => void guardar()} loading={guardando}>
            {zona ? "Guardar" : "Agregar"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <FormGrid className="sm:grid-cols-3">
          <Field label="Distrito" required error={errores.district} className="sm:col-span-3">
            <Input
              value={borrador.district}
              onChange={(e) => set("district", e.target.value)}
              placeholder="Miraflores"
              autoFocus
            />
          </Field>
        </FormGrid>

        <FormGrid>
          <Field label="Provincia">
            <Input value={borrador.province} onChange={(e) => set("province", e.target.value)} />
          </Field>
          <Field label="Departamento">
            <Input
              value={borrador.department}
              onChange={(e) => set("department", e.target.value)}
            />
          </Field>
        </FormGrid>

        <FormGrid>
          <Field label={`Tarifa (${moneda})`} required error={errores.price}>
            <Input
              inputMode="decimal"
              value={borrador.price}
              onChange={(e) => set("price", e.target.value)}
              placeholder="10.00"
            />
          </Field>
          <Field
            label={`Gratis desde (${moneda})`}
            hint="Vacío = nunca gratis."
            error={errores.free_from}
          >
            <Input
              inputMode="decimal"
              value={borrador.free_from}
              onChange={(e) => set("free_from", e.target.value)}
              placeholder="Sin umbral"
            />
          </Field>
        </FormGrid>

        <Field
          label="Tiempo estimado (minutos)"
          hint="Se le muestra al comprador al elegir su distrito."
          error={errores.estimated_minutes}
        >
          <Input
            inputMode="numeric"
            value={borrador.estimated_minutes}
            onChange={(e) => set("estimated_minutes", e.target.value)}
            placeholder="90"
          />
        </Field>

        <Toggle
          label="Activo"
          hint="Si lo desactivas, deja de aparecer en el checkout."
          checked={borrador.active}
          onChange={(v) => set("active", v)}
        />
      </div>
    </Modal>
  );
}
